import {DateString, NonEmptyString, TransactionIdIoTs} from '@raison/types';
import {TypeOf, array, boolean, keyof, nullType, number, string, type, union} from 'io-ts';
import {nullable, optional} from '~/library/io-ts-tools';

export type FiatTransactionType = TypeOf<typeof FiatTransactionType>;
export const FiatTransactionType = keyof({
    exchange_fiat: null,
    send: null,
    top_up_payment: null,
    top_up_card: null,
    top_up_bank: null,
    top_up_fedwire: null,
    top_up_paypal: null,
    withdraw_bank: null,
    withdraw_bik: null,
    withdraw_local_kzt: null,
    withdraw_card: null,
    withdraw_fee: null,
    referral_bonus: null,
});

export type TransactionTypeName = TypeOf<typeof TransactionTypeName>;
export const TransactionTypeName = keyof({
    ...FiatTransactionType.keys,
    fop_in: null,
    fop_out: null,
    buy: null,
    sell: null,
    exchange: null,
    exchange_otc: null,
    exchange_manual: null,
    receive: null,
    send_p2p: null,
    receive_p2p: null,
    top_up_correction: null,
    token_migrate: null,
    token_purchase: null,
    token_buy: null,
    token_sell: null,
    token_send: null,
    token_receive: null,
    token_refund: null,
    token_redemption: null,
    token_instant_redemption: null,
    securities_buy: null,
    securities_sell: null,
    securities_send: null,
    securities_receive: null,
    correction: null,
});

export type TransactionGroupName = TypeOf<typeof TransactionGroupName>;
export const TransactionGroupName = keyof({
    exchange: null,
    incomings: null,
    outgoings: null,
    unit_purchase: null,
});

export type TransactionStatus = TypeOf<typeof TransactionStatus>;
export const TransactionStatus = keyof({
    completed: null,
    moderate: null,
    pending: null,
    rejected: null,
});

export type TransactionSummaryItemStatus = TypeOf<typeof TransactionSummaryItemStatus>;
export const TransactionSummaryItemStatus = keyof({
    pending: null,
    moderate: null,
    in_progress: null,
    success: null,
    fail: null,
});

export type TransactionSummaryItem = TypeOf<typeof TransactionSummaryItem>;
export const TransactionSummaryItem = type({
    name: NonEmptyString,
    value: union([string, nullType]),
    copy: boolean,
    long: boolean,
    link: nullable(string),
    state: union([TransactionSummaryItemStatus, nullType]),
});

export type TransactionWeakSchema = TypeOf<typeof TransactionSchema>;
export const TransactionWeakSchema = type({
    id: TransactionIdIoTs,
    type: string,
});

export type TransactionSchema = TypeOf<typeof TransactionSchema>;
export const TransactionSchema = type({
    id: TransactionIdIoTs,
    type: TransactionTypeName,
    type_human: NonEmptyString,
    group: TransactionGroupName,
    group_human: NonEmptyString,
    status: TransactionStatus,
    status_human: NonEmptyString,
    volume: type({
        value: nullable(string),
        currency: NonEmptyString,
    }),
    flow: type({
        from: nullable(string),
        to: nullable(string),
    }),
    summary: array(array(TransactionSummaryItem)),
    rejected_message: nullable(string),
    created_at: DateString,
    is_low_value: optional(boolean),
});

export const PaginatedTransactionsSchema = type({
    orders: array(TransactionSchema),
    total: number,
    limit: number,
    last: nullable(number),
});
export type PaginatedTransactionsSchema = TypeOf<typeof PaginatedTransactionsSchema>;

// weakened validation so that the application does not fall with an unknown type of order
// https://jira.raison.digital/browse/RAISON-5333
export const PaginatedTransactionsWeakSchema = type({
    orders: array(TransactionWeakSchema),
});
export type PaginatedTransactionsWeakSchema = TypeOf<typeof PaginatedTransactionsWeakSchema>;
